/**
 * **ゾーンのキーの語彙**（Issue #522。`duelEditor/core/` ＝ 舞台の決まりごと）。盤の上の場所に付けた名前で、
 * フィールド生成の `overlay_zones`（`client/synchro-client/src/core/field_gen/playmat_zones.py`）と同じ綴り。
 *
 * ```
 * monster:{player}:{1..5}     モンスターゾーン
 * magic_trap:{player}:{1..5}  魔法罠ゾーン
 * field:{player}              フィールドゾーン
 * maindeck:{player}           デッキ
 * grave:{player}              墓地
 * removed:{player}            除外
 * extra:{player}              EX デッキ   ← 実写の Field は持たない（overlay_zones に無い）
 * hand:{player}               手札       ← 同上
 * ```
 *
 * `player` は core の team（**0 = 試合の P1 = 合成フィールド動画の下**）。
 * エクストラモンスターゾーンはこの動画のルールに無いので語彙に無い。
 */

/** 枠が 5 つ並ぶ行 */
export const ROW_KINDS = Object.freeze(['monster', 'magic_trap']);

/** 1 枚ぶんの場所 */
export const SINGLE_KINDS = Object.freeze(['field', 'maindeck', 'grave', 'removed', 'extra', 'hand']);

export const ZONE_KINDS = Object.freeze([...ROW_KINDS, ...SINGLE_KINDS]);

/** カードの縦横比（同梱プレイマットの枠 110 × 162） */
export const CARD_ASPECT = 110 / 162;

/**
 * ゾーンのキー。知らない種類は null。
 * @param {string} kind `ZONE_KINDS` のどれか
 * @param {number} player 0 | 1
 * @param {number} [seq] 0 起算の並び（`monster` / `magic_trap` だけ意味を持つ）
 */
export function zoneKey(kind, player, seq = 0) {
  const p = player === 1 ? 1 : 0;
  if (ROW_KINDS.includes(kind)) return seq >= 0 && seq < 5 ? `${kind}:${p}:${seq + 1}` : null;
  if (SINGLE_KINDS.includes(kind)) return `${kind}:${p}`;
  return null;
}

/** キー → `{ kind, player, seq }`（読めなければ null） */
export function parseZoneKey(key) {
  const m = /^([a-z_]+):([01])(?::([1-5]))?$/.exec(String(key ?? ''));
  if (!m) return null;
  const [, kind, p, seq] = m;
  if (ROW_KINDS.includes(kind)) return seq ? { kind, player: Number(p), seq: Number(seq) - 1 } : null;
  if (SINGLE_KINDS.includes(kind) && !seq) return { kind, player: Number(p), seq: 0 };
  return null;
}

/** 盤の上の場所ぜんぶ（この順に走査する）。Field が答えられないキーは呼ぶ側が飛ばす */
export const ZONE_SLOTS = Object.freeze([0, 1].flatMap((player) => [
  ...[0, 1, 2, 3, 4].flatMap((seq) => ROW_KINDS.map((kind) => ({ kind, player, seq }))),
  ...SINGLE_KINDS.map((kind) => ({ kind, player, seq: 0 })),
]));

/** そのプレイヤーの側（P1 = 手前）。実写でも仮想でも変わらない */
export const sideOf = (player) => (player === 1 ? 'top' : 'bottom');
